import { useAuthStore } from '@/stores/useAuthStore';
import { handleError } from '@/utils/handleError';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

export const useAuth = (needCheckAuth = false) => {
  const { isAuth, checkAuth, loading, user } = useAuthStore();
  const [isCheckingAuth, setIsCheckingAuth] = useState(needCheckAuth);
  const [isReady, setIsReady] = useState(!needCheckAuth);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Helper to get the search string from URL
  const getSearchString = useCallback(() => {
    return searchParams.toString() ? `?${searchParams.toString()}` : '';
  }, [searchParams]);

  // Perform auth check based on flag
  useEffect(() => {
    if (needCheckAuth) {
      const checkAuthentication = async () => {
        try {
          await checkAuth();
        } catch (error: unknown) {
          handleError(error);
        } finally {
          setIsCheckingAuth(false);
          setIsReady(true);
        }
      };
      checkAuthentication();
    }
  }, [checkAuth, needCheckAuth]);

  // Save current path including search params when auth is required but user isn't authenticated
  // Only for dashboard paths
  useEffect(() => {
    // Only save the path if the user is not authenticated and it's a dashboard path
    if (!isAuth && location.pathname.includes('/dashboard')) {
      const path = `${location.pathname}${getSearchString()}`;

      // Additional validation to make sure we only store valid dashboard paths
      if (
        path.includes('/dashboard/') &&
        !path.includes('/dashboard/login') &&
        !path.includes('/dashboard/register')
      ) {
        localStorage.setItem('last_path', path);
      }
    } else if (isAuth && !location.pathname.includes('/dashboard')) {
      // If user is authenticated and not on a dashboard page,
      // Check if we should preserve the last_path for accessory redirects
      const hasCodeParam = searchParams.has('code');
      const lastPath = localStorage.getItem('last_path');
      const isAccessoryPath = lastPath?.includes('/accessory/');

      if (!(hasCodeParam && isAccessoryPath)) {
        localStorage.removeItem('last_path');
      }
    }
  }, [isAuth, location.pathname, getSearchString, location, searchParams]);

  return {
    isAuth,
    isCheckingAuth,
    isReady,
    user,
    loading,
    getSearchString,
  };
};
